import {
  AppointmentRequestAgreementFragment,
  Input_Resource_AppointmentRequestMsg,
  Patientportal_PortalAppointmentRequestFailureReason,
  Resource_AppointmentLocation,
  Resource_AppointmentRequestActionType,
  Resource_AppointmentRequestSetting,
  Resource_AppointmentRequestStatus,
  Resource_AppointmentTimeFrameMsg,
  Resource_AppointmentTypeMsg,
  useCreateAppointmentRequestMutation,
  useFindCurrentPortalUserLazyQuery,
  useGetPortalFacilityHierarchyQuery,
  useSubmitAppointmentRequestMutation,
  useUpdateAppointmentRequestMutation,
} from '@/generated/graphql';
import { CheckCircleOutlined } from '@ant-design/icons';
import { AppointmentRequestAgreements } from '@components/slot-selection/appointment-request-agreements';
import { notification, Space } from 'antd';
import { Maybe } from 'graphql/jsutils/Maybe';
import { Moment } from 'moment';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { client } from '../../apollo/ApolloSetup';
import {
  APPOINTMENT_TYPE_FORM_KEY,
  FACILITY_FORM_KEY,
  PRE_SUBMISSION_FORM_KEY,
} from '../../constants/forms';
import { useHandleAppointmentRequestFailureReason } from '../../helpers/use-handle-appointment-request-failure-reason';
import { useCurrentUser } from '../../helpers/utils';
import { useCreateForm } from '../../hooks/use-create-form';
import { AveliosButton } from './avelios-button';

interface AppointmentRequestButtonProps {
  clinicId: Maybe<string> | undefined;
  officeHourId: Maybe<string> | undefined;
  note: Maybe<string> | undefined;
  timeFrameStart: Moment | null;
  timeFrameEnd: Moment | null;
  appointmentType: Maybe<Resource_AppointmentTypeMsg> | undefined;
  directBooking: Maybe<boolean> | undefined;
  slot: Maybe<Resource_AppointmentTimeFrameMsg> | undefined;
  externalLoading?: boolean;
  setExternalLoading?: (loading: boolean) => void;
  appointmentLocation: Maybe<Resource_AppointmentLocation> | undefined;
  // Will be triggered when a slot request fails
  onConfirmationFailure?: (requestedSlot: Resource_AppointmentTimeFrameMsg) => void;
}

const buildFormForwardUrl = (
  currentPath: string,
  appointmentRequestId: string,
  formRevisionGroupId: string,
) =>
  `${currentPath}&appointmentRequestId=${appointmentRequestId}&${PRE_SUBMISSION_FORM_KEY}=${formRevisionGroupId}`;

// TODO: If there is a pre-submission form ->
// create reserved appointment request + forward to form;
// else submit appointment request
export const AppointmentRequestButton: FC<AppointmentRequestButtonProps> = ({
  clinicId,
  appointmentType,
  officeHourId,
  slot,
  timeFrameStart,
  note,
  timeFrameEnd,
  directBooking,
  externalLoading,
  setExternalLoading,
  appointmentLocation,
  onConfirmationFailure,
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const { query } = router;
  const facilityId = query.facilityId as string;
  const appointmentRequestId = query.appointmentRequestId as string;
  const preSubmissionFormRevisionGroupId = query[PRE_SUBMISSION_FORM_KEY] as string;

  const [pageTransitioning, setPageTransitioning] = useState<boolean>(false);

  const { currentUser, userLoading } = useCurrentUser();

  const currentPath = router.asPath;

  const [createForm, { loading: createFormLoading }] = useCreateForm({}, currentPath);

  const [refreshUser] = useFindCurrentPortalUserLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const { data: facilityData } = useGetPortalFacilityHierarchyQuery({
    skip: !facilityId,
    variables: { rootPortalFacilityId: facilityId, depth: 0 },
  });
  const facility = facilityData?.getPortalFacilityHierarchy?.portalFacilities?.[0];
  const dataProtectionUrl = facility?.dataProtectionInformationUrl;

  const preSubmissionFormTemplateVersioningGroupId =
    appointmentType?.preSubmissionFormTemplateVersioningGroupId;

  const currentUserId = currentUser?.id;
  const verified = currentUser?.verified;

  const timeFrameRequest =
    appointmentType?.appointmentRequestConfig?.appointmentRequestSetting ===
    Resource_AppointmentRequestSetting.AppointmentRequestSettingTimeframeRequest;
  const slotRequest =
    appointmentType?.appointmentRequestConfig?.appointmentRequestSetting ===
    Resource_AppointmentRequestSetting.AppointmentRequestSettingSlotRequest;

  const disabled =
    !appointmentType?.id ||
    !clinicId ||
    (appointmentType.appointmentRequestConfig?.appointmentRequestSetting ===
      Resource_AppointmentRequestSetting.AppointmentRequestSettingSlotRequest &&
      !slot) ||
    (appointmentType.appointmentRequestConfig?.appointmentRequestSetting ===
      Resource_AppointmentRequestSetting.AppointmentRequestSettingTimeframeRequest &&
      (!timeFrameStart || !timeFrameEnd)) ||
    (!verified && currentUser?.accountVerificationRequiredForClinicIds?.includes(clinicId));

  const [createAppointmentRequestMutation, { loading: createRequestLoading }] =
    useCreateAppointmentRequestMutation();

  const [handleAppointmentRequestsFailureReason] = useHandleAppointmentRequestFailureReason();

  const [submitAppointmentRequestMutation, { loading: submitLoading }] =
    useSubmitAppointmentRequestMutation({
      onCompleted: (data) => {
        const failureReason = data?.submitAppointmentRequest?.failureReason;

        if (
          failureReason &&
          failureReason !==
            Patientportal_PortalAppointmentRequestFailureReason.PortalAppointmentRequestFailureReasonNone
        ) {
          handleAppointmentRequestsFailureReason(
            // re-submit request after handling completion
            failureReason,
            {
              next: handleSubmit,
              onError: () =>
                notification.error({
                  message: formatMessage({
                    id: 'appointment-request.error',
                  }),
                  description: formatMessage({
                    id: 'appointment-request.error.description',
                  }),
                }),
            },
          );
        } else {
          const appointmentRequest = data?.submitAppointmentRequest?.appointmentRequest;
          const clinicId = data?.submitAppointmentRequest?.clinicId;
          const status = appointmentRequest?.status;
          const autoRequestApproval =
            appointmentRequest?.appointmentType?.appointmentRequestConfig?.autoRequestApproval;

          if (!appointmentRequest) {
            if (onConfirmationFailure) {
              if (slot) {
                onConfirmationFailure(slot);
              } else {
                console.error('Error handling not defined');
              }
            } else {
              notification.error({
                message: formatMessage({
                  id: 'appointment-request.error',
                }),
                description: formatMessage({
                  id: 'appointment-request.error.description',
                }),
              });
              // @ts-ignore
              const rootQuery = client.cache.data.data.ROOT_QUERY;
              Object.keys(rootQuery).forEach((key) => {
                if (
                  key.match('getAvailableAppointmentSlots') ||
                  key.match('getAvailableAppointmentDates')
                ) {
                  client.cache.evict({ id: 'ROOT_QUERY', fieldName: key });
                }
              });
              return;
            }
          }
          refreshUser().then(({ data }) => {
            // Explicit refetch is required here since hook useCurrentUser is not being affected by manual refreshes due to network-only policy
            const userVerified = data?.findCurrentPortalUser?.portalUser?.verified;

            if (!userVerified) {
              setPageTransitioning(true);
              router
                .push('/appointment-request-account-verification')
                .then(() => setPageTransitioning(false));
              return;
            }

            // Appointment was booked automatically
            if (
              autoRequestApproval &&
              status === Resource_AppointmentRequestStatus.AppointmentRequestStatusFulfilled
            ) {
              // Automatic slot booking was enabled -> forward to success page (add automatic assignment flag)
              setPageTransitioning(true);
              router
                .push({
                  pathname: 'appointment-success',
                  query: {
                    appointmentRequestId: appointmentRequest?.id,
                    clinicId,
                    automatic: true,
                  },
                })
                .then(() => setPageTransitioning(false));
              return;
            }

            if (appointmentRequest) {
              notification.success({
                message: formatMessage({
                  id: 'appointment-request.success',
                }),
                description: formatMessage({
                  id: 'appointment-request.success.description',
                }),
              });

              setPageTransitioning(true);
              router
                .push({
                  pathname: '/appointments',
                })
                .then(() => setPageTransitioning(false));
            }
          });
        }
      },
    });

  const [appointmentRequestAgreements, setAppointmentRequestAgreements] =
    useState<AppointmentRequestAgreementFragment[]>();

  useEffect(() => {
    const appointmentRequestAgreements: AppointmentRequestAgreementFragment[] = (
      appointmentType?.agreementTemplates?.filterPresent() ?? []
    ).map((agreementTemplate) => ({
      accepted: false,
      template: agreementTemplate,
    }));

    setAppointmentRequestAgreements(appointmentRequestAgreements);
  }, [appointmentType]);

  const buildAppointmentRequest = () => {
    const formRevisionGroupIds: string[] = [];

    [FACILITY_FORM_KEY, APPOINTMENT_TYPE_FORM_KEY, PRE_SUBMISSION_FORM_KEY].forEach((formKey) => {
      const formRevisionGroupId = query[formKey] as string | undefined;
      if (!!formRevisionGroupId) {
        formRevisionGroupIds.push(formRevisionGroupId);
      }
    });

    const appointmentRequest: Input_Resource_AppointmentRequestMsg = {
      actionType: Resource_AppointmentRequestActionType.AppointmentRequestActionTypeBook,
      portalUserId: currentUser?.id,
      officeHourId,
      formRevisionGroupIds,
      appointmentType: { id: appointmentType?.id },
      requestPreferences: {
        exactTimeSlot: slotRequest
          ? {
              start: { seconds: slot?.startTime?.seconds },
              end: { seconds: slot?.endTime?.seconds },
            }
          : undefined,
        insideTimeFrame: timeFrameRequest
          ? {
              start: {
                seconds: timeFrameStart?.startOf('day')?.unix(),
              },
              end: { seconds: timeFrameEnd?.endOf('day')?.unix() },
            }
          : undefined,
        location: appointmentLocation,
      },
      comment: note,
      agreements: appointmentRequestAgreements?.filterPresent().map((agreement) => ({
        accepted: agreement?.accepted,
        template: { id: agreement?.template?.id },
      })),
    };

    return appointmentRequest;
  };

  const areAgreementsComplete = useMemo(
    () =>
      appointmentRequestAgreements
        ?.filter((agreement) => agreement?.template?.mandatory)
        ?.every((agreement) => agreement?.accepted) ?? true,
    [appointmentRequestAgreements],
  );

  const preSubmissionFormFilledOut = !!preSubmissionFormRevisionGroupId;

  const preSubmissionFormRequiredAndNotFilledOut =
    !!preSubmissionFormTemplateVersioningGroupId && !preSubmissionFormFilledOut;

  function submitAppointmentRequest() {
    if (clinicId) {
      if (appointmentRequestId) {
        submitAppointmentRequestMutation({
          variables: {
            clinicId,
            appointmentRequestId,
          },
        });
      } else {
        const appointmentRequest = buildAppointmentRequest();

        submitAppointmentRequestMutation({
          variables: {
            clinicId,
            appointmentRequest: {
              appointmentRequest,
            },
          },
        });
      }
    }
  }

  const [updateAppointmentRequestMutation] = useUpdateAppointmentRequestMutation();

  function prepareSubmission(
    portalUserId: string,
    preSubmissionFormTemplateVersioningGroupId: string,
  ) {
    if (clinicId) {
      const appointmentRequest = buildAppointmentRequest();

      createAppointmentRequestMutation({
        variables: {
          clinicId,
          portalUserId,
          appointmentRequest,
        },
      }).then((result) => {
        const data = result.data;

        const createdAppointmentRequestId = data?.createAppointmentRequest?.appointmentRequest?.id;

        if (createdAppointmentRequestId) {
          createForm(
            clinicId,
            preSubmissionFormTemplateVersioningGroupId,
            (preSubmissionFormRevisionGroupId) =>
              buildFormForwardUrl(
                currentPath,
                createdAppointmentRequestId,
                preSubmissionFormRevisionGroupId,
              ),
            (preSubmissionFormRevisionGroupId) => {
              if (preSubmissionFormRevisionGroupId) {
                const existingFormRevisionGroupIds = appointmentRequest?.formRevisionGroupIds;

                if (!existingFormRevisionGroupIds?.includes(preSubmissionFormRevisionGroupId)) {
                  updateAppointmentRequestMutation({
                    variables: {
                      clinicId,
                      portalUserId,
                      appointmentRequest: {
                        id: createdAppointmentRequestId,
                        formRevisionGroupIds: [
                          ...(existingFormRevisionGroupIds ?? []),
                          preSubmissionFormRevisionGroupId,
                        ], // patch update for string arrays
                      },
                    },
                  });
                }
              }
            },
          );
        }
      });
    }
  }

  function handleSubmit() {
    if (!!currentUserId) {
      if (preSubmissionFormTemplateVersioningGroupId && !preSubmissionFormFilledOut) {
        // Creates an appointment request (not submitted); creates a form; forwards to form with backlink
        prepareSubmission(currentUserId, preSubmissionFormTemplateVersioningGroupId);
      } else if (preSubmissionFormFilledOut || !preSubmissionFormTemplateVersioningGroupId) {
        submitAppointmentRequest();
      } else {
        console.error('Request failed', appointmentRequestId);
      }
    } else {
      router.push({
        pathname: '/login',
        query: { destination: router.asPath },
      });
    }
  }

  const loading = userLoading || submitLoading || pageTransitioning || createRequestLoading;

  useEffect(() => {
    if (externalLoading !== loading) {
      setExternalLoading?.(loading);
    }
  }, [loading]);

  return (
    <div>
      {(dataProtectionUrl ||
        (appointmentRequestAgreements && appointmentRequestAgreements.length > 0)) && (
        <div style={{ marginBottom: 20 }}>
          {dataProtectionUrl &&
            formatMessage(
              { id: 'notice.data-privacy' },
              {
                dataPrivacyInformation: (
                  <a href={dataProtectionUrl} target={'_blank'} rel={'noreferrer'}>
                    {formatMessage({
                      id: 'notice.data-privacy.privacy-information',
                    })}
                  </a>
                ),
              },
            )}
          {appointmentRequestAgreements && appointmentRequestAgreements.length > 0 && (
            <AppointmentRequestAgreements
              appointmentRequestAgreements={appointmentRequestAgreements}
              setAppointmentRequestAgreements={setAppointmentRequestAgreements}
            />
          )}
        </div>
      )}

      <Space direction={'vertical'} style={{ width: '100%' }}>
        {!!preSubmissionFormTemplateVersioningGroupId && (
          <div>
            <AveliosButton
              block
              disabled={preSubmissionFormFilledOut}
              loading={loading || createFormLoading}
              type={'primary'}
              size={'large'}
              onClick={handleSubmit}
              icon={preSubmissionFormFilledOut ? <CheckCircleOutlined /> : undefined}
              style={preSubmissionFormFilledOut ? { color: 'darkgreen' } : undefined}
            >
              {preSubmissionFormFilledOut
                ? formatMessage({ id: 'slot-selection.pre-submit-form.complete' })
                : formatMessage({ id: 'slot-selection.pre-submit-form' })}
            </AveliosButton>
          </div>
        )}
        <div>
          <AveliosButton
            loading={loading}
            type={'primary'}
            size={'large'}
            onClick={handleSubmit}
            block
            disabled={
              loading ||
              disabled ||
              preSubmissionFormRequiredAndNotFilledOut ||
              !areAgreementsComplete
            }
          >
            {directBooking
              ? formatMessage({
                  id: 'slot-selection.selected-slot.book',
                })
              : formatMessage({
                  id: 'facility-selection.appointment-request.request',
                })}
          </AveliosButton>
        </div>
      </Space>
    </div>
  );
};
